'use client';

import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { DirectionsIcon } from '@/shared/ui/display/EmojiIcon';
import type { DepartureLocationInfo } from '@/entities/order/model/order.types';
import type { PlannerDayCounsel } from '@/entities/visit-counsel/model/usePlannerDayCounsels';
import { CounselMap } from '@/widgets/visit-counsel/counsel-map';
import { openExternalMap } from '@/shared/lib/navigation/openExternalMap';

export interface CounselLocationMapProps {
  /** 방문 위치 좌표 (order.assignedDepartureLocation) */
  location: DepartureLocationInfo | null;
  /** 주소 문자열 (길찾기 시 장소명으로 사용) */
  address: string;
  visitCounselId: string;
  scheduleTime: string;
  /** 컨테이너 높이 채우기 (모바일 전체화면 지도용) */
  fill?: boolean;
  /** true면 길찾기 버튼 숨김 (상위에서 별도 렌더 시) */
  hideNavigateButton?: boolean;
  minHeight?: number;
}

/**
 * 상담 상세 - 이 상담 위치 하나만 표시하는 지도
 * 다른 상담 마커/루트 없음. 길찾기 버튼 오버레이.
 */
export function CounselLocationMap({
  location,
  address,
  visitCounselId,
  scheduleTime,
  fill = false,
  hideNavigateButton = false,
  minHeight = 220,
}: CounselLocationMapProps) {
  const counselsOverride = useMemo((): PlannerDayCounsel[] | undefined => {
    if (!location) return undefined;
    return [
      {
        visitCounselId,
        orderId: '',
        scheduleTime,
        location,
        type: 'current',
      },
    ];
  }, [location, visitCounselId, scheduleTime]);

  const handleNavigate = () => {
    if (location) {
      openExternalMap({
        latitude: location.latitude,
        longitude: location.longitude,
        name: address || '방문 상담',
      });
    }
  };

  if (!location) {
    return (
      <Box
        sx={{
          minHeight: fill ? 120 : minHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
          color: 'text.secondary',
          fontSize: '0.875rem',
        }}
      >
        좌표 정보 없음
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(fill ? { flex: 1, minHeight: 0 } : { minHeight }),
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CounselMap
        visitCounselId={null}
        assignedDepartureLocation={null}
        counselsOverride={counselsOverride}
        minHeight={minHeight}
        fill={fill}
      />
      {!hideNavigateButton && (
        <Button
          variant="contained"
          size="small"
          startIcon={<DirectionsIcon sx={{ fontSize: 18 }} />}
          onClick={handleNavigate}
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            minWidth: 48,
            minHeight: 48,
            boxShadow: 2,
            zIndex: 25,
            '&:hover': { boxShadow: 4 },
          }}
        >
          길찾기
        </Button>
      )}
    </Box>
  );
}
